import type { PricingSettings, StorefrontSettings } from "@workspace/api-client-react";
import { finalPriceBDT } from "./pricing";
import { estimateDeliveryBDT } from "./estimated-pricing";

export type Variant = {
  id: string;
  title: string;
  option1?: string | null;
  option2?: string | null;
  price: string;
  available: boolean;
  priceBDT?: number;
  compareAtPricePKR?: string | null;
  compareAtPriceBDT?: number | null;
  discountPercent?: number;
};
export type StoreProduct = {
  id: string;
  brandSlug?: string;
  title: string;
  handle: string;
  vendor: string;
  product_type: string;
  tags: string[] | string;
  variants: Variant[];
  images: { id: string; src: string; width?: number; height?: number }[];
  options?: { name: string; values: string[] }[];
  alwaysHideOptionNames?: string[];
  description?: string;
  body_html?: string | null;
  collection_handles?: string[];
  pieceCount?: number | null;
  minimumDiscountPercent?: number;
  isOnSale?: boolean;
  salePercent?: number;
  salePriceBDT?: number | null;
  originalPriceBDT?: number | null;
};
export type CartLine = { product: StoreProduct; variant: Variant; quantity: number };
export const CART_KEY = "chamakpatti-cart-v1";
const OLD_CATALOG_CACHE_PATTERN = /^chamakpatti-[a-z0-9-]+-catalog-v\d+$/i;
const ALLOWED_COLLECTIONS = new Set([
  "women-eastern-pret-view-all-sale",
  "women-unstitched-view-all-sale",
]);
const excludedProductText = /\b(kids?|kid's|children|childrens|infants?|toddlers?|boys?|girls?|western|footwear|shoes?|sandals?|fragrances?|perfumes?|accessor(?:y|ies)|jewel(?:l?ery|ry)|handbags?|purses?|bags?|wallets?|belts?|watches?|sunglasses|beauty|cosmetics?|makeup|homeware|bedding|gift\s*cards?)\b|east[\s-]*meets[\s-]*west|home[\s-]*decor/i;

export const linePrice = (
  line: CartLine,
  pricing?: PricingSettings,
  deliverySettings?: Pick<StorefrontSettings, "shippingRatePerKgBDT" | "pieceCountWeightsKg" | "defaultDeliveryWeightKg" | "deliveryWithoutPieceCountMode">,
) => (finalPriceBDT(line.variant.price, pricing)
  + (deliverySettings ? estimateDeliveryBDT(line.product, deliverySettings) : 0)) * line.quantity;
export const cartLineKey = (line: Pick<CartLine, "product" | "variant">) =>
  `${line.product.brandSlug ?? "ethnc"}:${String(line.variant.id)}`;
export const slugify = (text: string) => text.toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const EASTERN_MENU = [
  { label: "Eastern", children: ["Pret", "Unstitched"] },
] as const;
export const EASTERN_EDIT = ["Rozana", "Casual", "Luxe", "Unstitched", "Special Price"] as const;
export function menuCategoryFromSlug(slug?: string) {
  if (!slug) return undefined;
  return [...EASTERN_MENU.flatMap(item => [item.label, ...item.children]), ...EASTERN_EDIT]
    .find(label => slugify(label) === slug);
}

export function isEasternProduct(product: StoreProduct) {
  const tags = Array.isArray(product.tags) ? product.tags : product.tags.split(",");
  const text = `${product.product_type} ${product.title} ${tags.join(" ")}`.toLowerCase();
  if (excludedProductText.test(text)) return false;
  const easternCollection = product.collection_handles?.some(handle => ALLOWED_COLLECTIONS.has(handle)) ?? false;
  const easternTag = tags.some(tag => tag.trim().toLowerCase() === "women-eastern");
  return easternCollection || easternTag;
}

export function readCart(): CartLine[] {
  try {
    const stored = JSON.parse(localStorage.getItem(CART_KEY) || "[]") as CartLine[];
    if (!Array.isArray(stored)) return [];
    const migrated = stored.map(line => {
      if (!line?.product || !line?.variant) return line;
      const product = {
        ...line.product,
        id: String(line.product.id),
        variants: Array.isArray(line.product.variants)
          ? line.product.variants.map(variant => ({ ...variant, id: String(variant.id) }))
          : [],
        images: Array.isArray(line.product.images)
          ? line.product.images.map(image => ({ ...image, id: String(image.id) }))
          : [],
      };
      const variantId = String(line.variant.id);
      const variant = product.variants.find(candidate => candidate.id === variantId)
        ?? { ...line.variant, id: variantId };
      const normalized = { ...line, product, variant };
      return product.brandSlug
        && product.brandSlug !== "ethnc"
        && isEasternProduct(product)
        ? { ...normalized, product: { ...product, brandSlug: "ethnc" } }
        : normalized;
    });
    return migrated.filter(line => line?.product && (line.product.brandSlug || isEasternProduct(line.product)));
  } catch {
    return [];
  }
}

export function removeOldCatalogCaches() {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key && OLD_CATALOG_CACHE_PATTERN.test(key)) localStorage.removeItem(key);
    }
  } catch {
    // Browser storage is optional.
  }
}